import { SUPABASE_URL } from '../utils/supabase/url';
/** Outreach service — wraps the /outreach edge-function routes.
 *  No caching: the approvals queue must never show a stale batch.
 *
 *  Every method takes workspaceId first. requireOutreachWorkspaceAccess on
 *  the server rejects any call missing it, so there is no optional-workspace
 *  fallback to design around here. */

import { projectId as supabaseProjectId } from '../utils/supabase/info';
import { getAuthHeaders } from '../utils/supabase/client';
import type {
  Batch,
  BatchSummary,
  OutreachSettings,
  OutreachTemplate,
  Prospect,
  Suppression,
  TouchWithVerdict,
} from '../types/outreach';

const API_BASE = `${SUPABASE_URL}/functions/v1/make-server-a476273b`;

/** Thrown by authedFetch on a non-OK response. `status` lets a caller branch
 *  on the HTTP status (a 409 version conflict, a 403 internal-only refusal)
 *  instead of string-matching the route's error text, which breaks silently
 *  the moment that wording changes. See DocumentMarkupsService.ts and
 *  PersonalAccessTokenService.ts for the same pattern elsewhere. */
export interface OutreachServiceError extends Error {
  status: number;
}

async function authedFetch(url: string, init?: RequestInit): Promise<Response> {
  const headers = await getAuthHeaders({ 'Content-Type': 'application/json' });
  const res = await fetch(url, { ...init, headers: { ...headers, ...(init?.headers ?? {}) } });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw Object.assign(
      new Error((body as { error?: string }).error ?? `Request failed (${res.status})`),
      { status: res.status },
    ) as OutreachServiceError;
  }
  return res;
}

export const OutreachService = {
  async listProspects(
    workspaceId: string,
    opts: { status?: string; followUpOverdue?: boolean; limit?: number; offset?: number } = {},
  ): Promise<{ prospects: Prospect[]; total: number }> {
    const url = new URL(`${API_BASE}/outreach/prospects`);
    url.searchParams.set('workspaceId', workspaceId);
    if (opts.status) url.searchParams.set('status', opts.status);
    if (opts.followUpOverdue) url.searchParams.set('followUpOverdue', '1');
    if (opts.limit != null) url.searchParams.set('limit', String(opts.limit));
    if (opts.offset != null) url.searchParams.set('offset', String(opts.offset));
    const res = await authedFetch(url.toString());
    return await res.json() as { prospects: Prospect[]; total: number };
  },

  async getProspect(workspaceId: string, id: string): Promise<Prospect> {
    const url = new URL(`${API_BASE}/outreach/prospects/${encodeURIComponent(id)}`);
    url.searchParams.set('workspaceId', workspaceId);
    const res = await authedFetch(url.toString());
    const data = await res.json() as { prospect: Prospect };
    return data.prospect;
  },

  async updateProspect(
    workspaceId: string,
    id: string,
    expectedVersion: number,
    changes: Partial<Prospect>,
  ): Promise<Prospect> {
    const res = await authedFetch(`${API_BASE}/outreach/prospects/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      body: JSON.stringify({ workspaceId, expectedVersion, changes }),
    });
    const data = await res.json() as { prospect: Prospect };
    return data.prospect;
  },

  // Re-verifies against Companies House and refreshes the verified block
  // (legalForm, chStatus, verifiedAt, ...). No body: the prospect id in the
  // path and workspaceId in the query are the only inputs the route reads.
  async qualifyProspect(workspaceId: string, id: string): Promise<Prospect> {
    const url = new URL(`${API_BASE}/outreach/prospects/${encodeURIComponent(id)}/qualify`);
    url.searchParams.set('workspaceId', workspaceId);
    const res = await authedFetch(url.toString(), { method: 'POST' });
    const data = await res.json() as { prospect: Prospect };
    return data.prospect;
  },

  // Internal-only on the server (403 for a workspace caller): promotes a
  // replied prospect onto the Enquiries board. workspaceId is a required
  // query param, not optional, so a mismatched id/workspace pair 404s
  // instead of promoting the wrong record.
  async promoteProspect(workspaceId: string, id: string): Promise<{ dealId: string }> {
    const url = new URL(`${API_BASE}/outreach/prospects/${encodeURIComponent(id)}/promote`);
    url.searchParams.set('workspaceId', workspaceId);
    const res = await authedFetch(url.toString(), { method: 'POST' });
    return await res.json() as { dealId: string };
  },

  async listBatches(
    workspaceId: string,
    opts: { status?: string; limit?: number; offset?: number } = {},
  ): Promise<BatchSummary[]> {
    const url = new URL(`${API_BASE}/outreach/batches`);
    url.searchParams.set('workspaceId', workspaceId);
    if (opts.status) url.searchParams.set('status', opts.status);
    if (opts.limit != null) url.searchParams.set('limit', String(opts.limit));
    if (opts.offset != null) url.searchParams.set('offset', String(opts.offset));
    const res = await authedFetch(url.toString());
    const data = await res.json() as { batches: BatchSummary[] };
    return data.batches ?? [];
  },

  /** Bundles every unsubmitted draft in the workspace into a pending batch for human review. Approves and sends nothing. */
  async submitBatch(workspaceId: string): Promise<void> {
    await authedFetch(`${API_BASE}/outreach/batches`, {
      method: 'POST',
      body: JSON.stringify({ workspaceId, note: null }),
    });
  },

  async getBatch(workspaceId: string, id: string): Promise<{ batch: Batch; touches: TouchWithVerdict[] }> {
    const url = new URL(`${API_BASE}/outreach/batches/${encodeURIComponent(id)}`);
    url.searchParams.set('workspaceId', workspaceId);
    const res = await authedFetch(url.toString());
    return await res.json() as { batch: Batch; touches: TouchWithVerdict[] };
  },

  async approveBatch(
    workspaceId: string,
    id: string,
    touchIds?: string[],
  ): Promise<{ approved: number; refused: { touchId: string; reason: string }[] }> {
    const res = await authedFetch(`${API_BASE}/outreach/batches/${encodeURIComponent(id)}/approve`, {
      method: 'POST',
      body: JSON.stringify({ workspaceId, touchIds }),
    });
    return await res.json() as { approved: number; refused: { touchId: string; reason: string }[] };
  },

  async rejectBatch(
    workspaceId: string,
    id: string,
    reason: string,
    touchIds?: string[],
  ): Promise<{ rejected: number }> {
    const res = await authedFetch(`${API_BASE}/outreach/batches/${encodeURIComponent(id)}/reject`, {
      method: 'POST',
      body: JSON.stringify({ workspaceId, reason, touchIds }),
    });
    return await res.json() as { rejected: number };
  },

  async listSuppressions(
    workspaceId: string,
    opts: { limit?: number; offset?: number } = {},
  ): Promise<{ suppressions: Suppression[]; total: number }> {
    const url = new URL(`${API_BASE}/outreach/suppressions`);
    url.searchParams.set('workspaceId', workspaceId);
    if (opts.limit != null) url.searchParams.set('limit', String(opts.limit));
    if (opts.offset != null) url.searchParams.set('offset', String(opts.offset));
    const res = await authedFetch(url.toString());
    return await res.json() as { suppressions: Suppression[]; total: number };
  },

  // Immediate and permanent: there is deliberately no unsuppress method
  // anywhere in this service, matching the route surface (Section 7).
  async addSuppression(
    workspaceId: string,
    params: { email?: string; companiesHouseNumber?: string; companyName?: string; reason: string },
  ): Promise<Suppression | null> {
    const res = await authedFetch(`${API_BASE}/outreach/suppressions`, {
      method: 'POST',
      body: JSON.stringify({ workspaceId, ...params }),
    });
    const data = await res.json() as { suppression: Suppression | null };
    return data.suppression;
  },

  async listTemplates(
    workspaceId: string,
    opts: { limit?: number; offset?: number } = {},
  ): Promise<OutreachTemplate[]> {
    const url = new URL(`${API_BASE}/outreach/templates`);
    url.searchParams.set('workspaceId', workspaceId);
    if (opts.limit != null) url.searchParams.set('limit', String(opts.limit));
    if (opts.offset != null) url.searchParams.set('offset', String(opts.offset));
    const res = await authedFetch(url.toString());
    const data = await res.json() as { templates: OutreachTemplate[] };
    return data.templates ?? [];
  },

  async updateTemplate(
    workspaceId: string,
    templateId: string,
    expectedVersion: number,
    subject: string,
    bodyText: string,
  ): Promise<OutreachTemplate> {
    const res = await authedFetch(`${API_BASE}/outreach/templates/${encodeURIComponent(templateId)}`, {
      method: 'PUT',
      body: JSON.stringify({ workspaceId, expectedVersion, subject, bodyText }),
    });
    const data = await res.json() as { template: OutreachTemplate };
    return data.template;
  },

  // Settings has no `version` column, so there is no optimistic-concurrency
  // argument here the way updateProspect/updateTemplate take one.
  async getSettings(workspaceId: string): Promise<OutreachSettings | null> {
    const url = new URL(`${API_BASE}/outreach/settings`);
    url.searchParams.set('workspaceId', workspaceId);
    const res = await authedFetch(url.toString());
    const data = await res.json() as { settings: OutreachSettings | null };
    return data.settings ?? null;
  },

  // Internal-only on the server (403 for a workspace caller) — banned_phrases
  // is a policy decision, not a self-serve toggle. See the grant comment on
  // outreach_settings in 20260916110000_outreach_v1.sql.
  async updateSettings(workspaceId: string, changes: Partial<OutreachSettings>): Promise<OutreachSettings> {
    const res = await authedFetch(`${API_BASE}/outreach/settings`, {
      method: 'PATCH',
      body: JSON.stringify({ workspaceId, changes }),
    });
    const data = await res.json() as { settings: OutreachSettings };
    return data.settings;
  },
};
